import { type FC, useState, lazy, Suspense } from 'react';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import LikesIcon from '../Likes/LikesIcon.tsx';
import Dropdown, { alignConstants, sizeConstants } from '../Dropdown/Dropdown.tsx';
import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent.tsx';
import DropdownBody from '../Dropdown/DropdownBody.tsx';
import { FavoritesDropdownEmpty } from './FavoritesDropdownEmpty.tsx';

import { trackUserNotifications } from '../Notifications/userNotifications/userNotificationsTracking.ts';

import styles from './Favorites.scss';
import dibsCss from 'dibs-css';

const FavoritesDropdown = lazy(
    () => import(/* webpackChunkName: "FavoritesDropdown" */ './FavoritesDropdown.tsx')
);

const FAVORITES_URL = '/favorites/items/';

const Favorites: FC<{
    hasRootPendingRefetch: boolean;
    hasUserId: boolean;
    isClient: boolean;
    userId: string;
}> = ({ hasRootPendingRefetch, hasUserId, isClient, userId }) => {
    const [showFavorites, setShowFavorites] = useState(false);

    if (!isClient) {
        return <LikesIcon favoritesUrl={FAVORITES_URL} />;
    }

    const fetchFavorites = !hasRootPendingRefetch;

    return (
        <Dropdown
            align={alignConstants.ALIGN_RIGHT}
            onShow={() => setShowFavorites(true)}
            onHide={() => setShowFavorites(false)}
            size={sizeConstants.SIZE_EXTRA_LARGE}
            dropdownClassName={styles.dropdown}
        >
            <LikesIcon favoritesUrl={FAVORITES_URL} />
            {showFavorites && (
                <DropdownBody setMaxHeight={false}>
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.itemsCenter,
                            dibsCss.justifyBetween,
                            dibsCss.pSmall,
                            dibsCss.borderB,
                            dibsCss.borderBSolid,
                            dibsCss.borderBDolphin
                        )}
                    >
                        <HeadingLevel>
                            {Heading => (
                                <Heading
                                    className={classnames(dibsCss.textBodyLargeHeavy, dibsCss.m0)}
                                >
                                    <FormattedMessage
                                        id="dbl.FavoritesDropdown.title"
                                        defaultMessage="Your Favorites"
                                    />
                                </Heading>
                            )}
                        </HeadingLevel>
                        <Button
                            size="small"
                            type="standard"
                            style="circle"
                            href="/favorites/items/"
                            onClick={() =>
                                trackUserNotifications({
                                    stepInteractionName: 'header favorites notification click',
                                    trigger: 'view all',
                                })
                            }
                        >
                            <FormattedMessage
                                id="dbl.FavoritesDropdown.viewAll"
                                defaultMessage="View All Favorites"
                            />
                        </Button>
                    </div>
                    <Suspense fallback={<DropdownLoadingContent />}>
                        {!hasUserId && <FavoritesDropdownEmpty />}
                        {hasUserId && !fetchFavorites && <DropdownLoadingContent />}
                        {hasUserId && fetchFavorites && <FavoritesDropdown userId={userId} />}
                    </Suspense>
                </DropdownBody>
            )}
        </Dropdown>
    );
};

export default Favorites;
